import React from 'react';
import { uuid } from '../../../utils/utils';
import EventSequence from '../../../@types/event-sequence';
import EventSequenceService from '../../../services/event-sequence.service';
import { toast } from 'react-toastify';
import {EventSequenceSummaryEditorProps} from '../../../@types/editor-props';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Histogram, UncertaintyAnalysisResult } from '../../../@types/uncertainty-analysis';
import {
    Accordion, AccordionDetails, AccordionSummary, AppBar,
    Backdrop, Button, FormGroup, Paper, Stack, TextField,
    Typography
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { InputAdornment } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { Bar } from 'react-chartjs-2';
import { Fullscreen } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import Toolbar from '@mui/material/Toolbar';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoIcon from '@mui/icons-material/Info'

import MuiTooltip from '@mui/material/Tooltip';
import {
    Chart,
    BarController,
    BarElement,
    Tooltip,
    CategoryScale,
    LinearScale,
} from 'chart.js';

Chart.register(BarController, BarElement, Tooltip, CategoryScale, LinearScale);

interface Column {
    id: keyof UncertaintyAnalysisResult;
    label: string;
    minWidth?: number;
    align?: 'left' | 'right' | 'center';
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { id: 'uniqueId', label: 'Unique Id', minWidth: 150, align: 'left'},
    { id: 'name', label: 'Name', minWidth: 150, align: 'left' },
    { id: 'pedigree', label: 'Pedigree', minWidth: 100, align: 'left' },
    { id: 'baseline', label: 'Baseline', minWidth: 50, align: 'center' },
    { id: 'mean', label: 'Mean', minWidth: 50, align: 'center' },
    { id: 'standardDeviation', label: 'Standard Deviation', minWidth: 50, align: 'center' },
    { id: 'coefficientOfVariation', label: 'Coefficient of Variation', minWidth: 50, align: 'center' },
    { id: 'histogram', label: 'Distribution', minWidth: 250, align: 'left' },
];

type CVColor = 'success' | 'warning' | 'error';
type Order = 'asc' | 'desc';

const categorizeCV = function(cv: number): CVColor {
    if (cv <= 0.3) {
        return 'success';
    }
    if (cv <= 0.7) {
        return 'warning';
    }
    return 'error';
};

function isNumeric(n: any): boolean {
    return !isNaN(parseFloat(n)) && isFinite(n);
}

function toExponent(x: any): string {
    if (isNumeric(x)) {
        if (Number.isInteger(x)) {
            return x.toString();
        } else {
            return parseFloat(x).toExponential(2).toString();
        }
    }
    return x;
}


const getLabels = function (results: Histogram): any {
    const { minValue, bins, binWidth } = results;
    return Object.keys(bins).map((binNumber) => {
        const lowerBound = parseInt(binNumber) * binWidth + minValue;
        const upperBound = (parseInt(binNumber) + 1) * binWidth + minValue;
        return `[${toExponent([lowerBound])}, ${toExponent([upperBound])}]`;
    });
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator<Key extends keyof UncertaintyAnalysisResult>(
    order: Order,
    orderBy: Key,
): (
  a: UncertaintyAnalysisResult,
  b: UncertaintyAnalysisResult,
) => number {
    return order === 'desc'
        ? (a, b): number => descendingComparator(a, b, orderBy)
        : (a, b): number => -descendingComparator(a, b, orderBy);
}

function SortableCell(props: { column: Column, order: Order, orderBy: string, onRequestSort: (event: React.MouseEvent<unknown>, property: keyof UncertaintyAnalysisResult) => void }): React.ReactElement {
    const { column, order, orderBy, onRequestSort } = props;
    const createSortHandler = (property: keyof UncertaintyAnalysisResult) => (event: React.MouseEvent<unknown>): void => {
        onRequestSort(event, property);
    };
    return (
        <TableCell
            key={column.id}
            align={column.align}
            style={{ minWidth: column.minWidth }}
            sortDirection={orderBy === column.id ? order : false}
        >
            <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : 'asc'}
                onClick={createSortHandler(column.id)}
            >
                {column.label}
            </TableSortLabel>
        </TableCell>
    );
}

function Intro(): React.ReactElement {
    return (
        <Box sx={{ml: 1}}>
            <Typography variant="h6" gutterBottom>
                Motivation
            </Typography>
            <Typography variant="body2" gutterBottom>
                Event trees are often used in the assessment of aviation safety. Advantages of event trees are that they
                are straight-forward to evaluate and easy to understand. In safety-related applications,
                one challenge is that there may be little historical data that can be used to quantify the parameters of the
                model. Since catastrophic accidents are rare, the number of observed events available to quantify the
                model may be small or even zero. In particular, some sequences of an event tree may have never
                occurred, and thus appropriate probabilities along those event paths are estimated to be zero.
            </Typography>
            <Typography variant="body2" gutterBottom>
                But, just because an event has not occurred in the past, does not mean that it will never occur in the
                future. This means that there is some uncertainty around the probability estimates that are quantified via
                the data. This issue is particularly significant for rare-event problems, because the uncertainty ranges can
                be very large relative to the true underlying probabilities (i.e., a high relative uncertainty). Even though
                the uncertainty range narrows with more observations – a sample size of 1,000,000 flights gives a better
                estimate than a sample size of 1,000 – there is always some degree of uncertainty, no matter how many
                observations are collected.
            </Typography>
            <Typography variant="body2" gutterBottom>
                ISAM provides a point estimate for each branching probability in the event tree. For example,
                if no events have been observed along a particular branch, then the point estimate for that branch is zero.
                The goal of the uncertainty analysis capability is instead to provide an uncertainty distribution for each branching
                probability,
                which provides a range of possible values. The uncertainty distribution is quantified based on the
                observed data in the end events.
            </Typography>

            <Typography variant="h6" gutterBottom>
                Method
            </Typography>
            <Typography variant="body2" gutterBottom>
                The uncertainty analysis method is based on an approach for
                constructing exact confidence intervals for the Poisson distribution. The approach also uses a Bayesian
                updating procedure for specifying the distribution for the mean parameter of a Poisson distribution. A flow chart
                for the algorithm is shown below.
            </Typography>
            <Box sx={{ display: 'flex'}}>
                <img src={'./images/uncertainty_algorithm.png'} alt='Uncertainty Analysis Algorithm' />
            </Box>
            <Typography variant="body2" gutterBottom>
                The theory behind the method is discussed in the SSMT report "Uncertainty Assessment for the Integrated
                Safety Assessment Model", Shortle, John, 2020.
            </Typography>

            <Typography variant="h6" gutterBottom>
                Interpreting the results
            </Typography>
            <Box sx={{ display: 'flex'}}>
                <img src={'./images/uncertainty_chart_examples.png'} alt='Uncertainty Chart Examples' />
                <Box>
                    <Typography variant='subtitle1' style={{marginTop: '140px', paddingLeft: '25px'}}>
                        An example distribution of a probability value with a <b>lower degree of uncertainty.</b>
                    </Typography>
                    <Typography variant='subtitle1' style={{marginTop: '180px', paddingLeft: '25px'}}>An example distribution of a probability value with a <b>higher degree of uncertainty.</b></Typography>
                </Box>
            </Box>
            <Box style={{ marginTop: '30px' }}>
                <img src={'./images/uncertainty_chart_anatomy.png'} alt='Uncertainty Chart Anatomy' />
            </Box>

            <Typography variant="body2" gutterBottom>

            </Typography>
            <Typography variant="body2" gutterBottom>

            </Typography>

        </Box>
    );
}

export default function UncertaintyAnalysisEditor(props: EventSequenceSummaryEditorProps): React.ReactElement {
    const { eventSequenceSummary,  isActive } = props;
    const [uncertaintyAnalysisResults, setUncertaintyAnalysisResults] = React.useState<UncertaintyAnalysisResult[]>([]);
    const [simulations, setSimulations] = React.useState<number>(1000);
    const [exposure, setExposure] = React.useState<number>(100000000);
    const [bins, setBins] = React.useState<number>(100);
    const [isRunning, setIsRunning] = React.useState<boolean>(false);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [originalResults, setOriginalResults] = React.useState<UncertaintyAnalysisResult[]>([]);
    const [fullscreenMode, setFullscreenMode] = React.useState<boolean>(false);
    const [sidePanelOpen, setSidePanelOpen] = React.useState<boolean>(false);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof UncertaintyAnalysisResult>('uniqueId');
    const uuidRef = React.useRef(uuid());

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof UncertaintyAnalysisResult,
    ): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };    

    const toggleSidePanel = (): void => {
        setSidePanelOpen(!sidePanelOpen);
    }

    const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(event.target.value);
    }

    const handleRunUncertaintyAnalysis = (): void => {
        let uncertaintyAnalysisPromise = EventSequenceService.fetchUncertaintyAnalysis(eventSequenceSummary.id, simulations, exposure, bins);
        let eventSequencePromise = EventSequenceService.getEventSequenceById(eventSequenceSummary.id);
        setIsRunning(true);
        Promise.all([uncertaintyAnalysisPromise, eventSequencePromise])
            .then((responses) => {
                let results = responses[0].eventUncertaintyAnalysisResults.map((result) => {
                    const event = responses[1].events.find((event: EventSequence) => event.id === result.id);
                    let qval = ['INITIATING', 'END', 'INPRECURSOR'].includes(event.type) ? event.frequency : event.probability;
                    return {
                        id: result.id,
                        uniqueId: event.uniqueId,
                        name: event.name,
                        pedigree: event!.pedigree,
                        baseline: qval,
                        histogram: result.histogram,
                        mean: result.mean,
                        standardDeviation: result.standardDeviation,
                        coefficientOfVariation: result.coefficientOfVariation
                    };
                }).sort((a, b) => a.uniqueId.localeCompare(b.uniqueId));
                setUncertaintyAnalysisResults(results.filter((result) => {
                    return result.uniqueId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        result.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        result.pedigree.toLowerCase().includes(searchTerm.toLowerCase());
                }));
                setOriginalResults(results);
                setSidePanelOpen(false);
            })
            .catch((error) => {
                toast.error(`Failed to load event sequence: ${error}`);
            }).finally(() => {
                setIsRunning(false);
            });
    }

    React.useEffect(() => {
        if (isActive) {
            handleRunUncertaintyAnalysis();
        }
    }, [isActive]);

    React.useEffect(() => {
        setUncertaintyAnalysisResults(originalResults.filter((result) => {
            return result.uniqueId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                result.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                result.pedigree.toLowerCase().includes(searchTerm.toLowerCase());
        }));
    }, [searchTerm]);

    
    React.useEffect(() => {
        return setUncertaintyAnalysisResults(originalResults.sort(getComparator(order, orderBy)).filter((result) => {
            return result.uniqueId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                result.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                result.pedigree.toLowerCase().includes(searchTerm.toLowerCase());
        }));
    }, [order, orderBy]);

    return (
        <Paper id={uuidRef.current}   sx={{ height: '100%'}}>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isRunning}> 
                <CircularProgress color='inherit' />
            </Backdrop>
            
            <div className={`side-panel-content ${sidePanelOpen ? 'side-panel-open' : ''}`} style={{ height: '100%'}}>
                <AppBar position="static"
                    sx={{
                        width: '100%',
                        borderRadius: '0px 10px 0px 0px',
                        backgroundColor: '#fff',
                        color: '#808080'
                    }}
                >
                    <Toolbar
                        sx={[
                            {
                                pl: { sm: 2 },
                                pr: { xs: 1, sm: 1 },
                                flexGrow: 1
                            }
                        ]}
                    >
                        <Typography
                            component='div'
                            variant='h6'
                            color='inherit'
                            sx={{ flex: '1 1 100%' }}
                        >
                        Simulation Options
                        </Typography>
                        <IconButton
                            color="inherit"
                            component='button'
                            edge='end'
                            onClick={toggleSidePanel} >
                            <CloseIcon fontSize='small'/>
                        </IconButton>
                    </Toolbar>

                </AppBar>
                <FormGroup className='formStyle' style={{width: '100%'}}>
                    <Stack className='formControl' style={{ width: '80%' }}>
                        <Box sx={{display: 'flex', direction: 'row'}}>
                            <TextField
                                id={`simulation-input-${uuidRef.current}`}
                                label='Simulations'
                                aria-label='Simulations'
                                type='number'
                                defaultValue={simulations}
                                onChange={(event): void => setSimulations(parseInt(event.target.value))}
                                inputProps={{
                                    min: 1,
                                    'tabIndex': 0,
                                    'aria-labelledby': `simulation-input-${uuidRef.current}`,
                                    'aria-label': 'Simulations'
                                }}
                                style={{flexGrow: 1}}
                            />
                            <MuiTooltip
                                title="The number of simulations that will be run to generate the uncertainty analysis"
                                placement="left-start"
                                arrow
                            >
                                <InfoIcon color='primary' fontSize='small' sx={{ ml: 1, mt: 2 }}/>
                            </MuiTooltip>
                        </Box>
                        <hr />
                        <Box sx={{display: 'flex', direction: 'row'}}>
                            <TextField
                                id={`exposure-input-${uuidRef.current}`}
                                label='Exposure'
                                aria-label='Exposure'
                                type='number'
                                onChange={(event): void => setExposure(parseInt(event.target.value))}
                                defaultValue={exposure}
                                inputProps={{
                                    min: 1,
                                    step: 1,
                                    'tabIndex': 0,
                                    'aria-labelledby': `exposure-input-${uuidRef.current}`,
                                    'aria-label': 'Exposure'
                                }}
                                style={{flexGrow: 1}}
                            />
                            <MuiTooltip
                                title="This is the number that was used as the denominator to figure
                                 out the initiating event frequency e.g. the number of flights or 
                                flight hours in a certain time period"
                                placement="left-start"
                                arrow
                            >
                                <InfoIcon color='primary' fontSize='small' sx={{ ml: 1, mt: 2 }} />
                            </MuiTooltip>

                        </Box>
                        <hr />
                        <Box sx={{ display: 'flex', direction: 'row' }}>
                            <TextField
                                id={`bin-input-${uuidRef.current}`}
                                label='Bins'
                                aria-label='Bins'
                                type='number'
                                onChange={(event): void => setBins(parseInt(event.target.value))}
                                defaultValue={bins}
                                inputProps={{
                                    min: 1,
                                    step: 1,
                                    'tabIndex': 0,
                                    'aria-labelledby': `bin-input-${uuidRef.current}`,
                                    'aria-label': 'Bins'
                                }}
                                style={{flexGrow: 1}}
                            />
                            <MuiTooltip
                                title="The number of bins in each histogram"
                                placement="left-start"
                                arrow
                            >
                                <InfoIcon color='primary' fontSize='small' sx={{ ml: 1, mt: 2 }}/>
                            </MuiTooltip>                           
                        </Box>
                        <hr />
                    </Stack>
                    <MuiTooltip
                        title="Note: Setting the Bins or Simulations to a high value will impact performance."
                    >
                        <Button
                            className='formBtn'
                            id={`run-uncertainty-analysis-${uuidRef.current}`}
                            variant='outlined'
                            color='primary'
                            onClick={(): void => { handleRunUncertaintyAnalysis(); }}
                            startIcon={<BarChartIcon />}
                            sx={{maxWidth: '200px', margin: 'auto'}}
                        >
                                Analyze
                        </Button>

                    </MuiTooltip>

                </FormGroup>              
            </div>
            <div className={`side-panel-handle touch-action ${sidePanelOpen ? 'side-panel-open' : ''}`} onClick={toggleSidePanel} style={{ cursor: 'pointer' }}>Options</div>
            <Accordion disableGutters>
                <AccordionSummary
                    aria-controls='uncertainty-analysis-intro'
                    id={`uncertainty-analysis-intro-${uuidRef.current}`}
                    expandIcon={<ExpandMoreIcon />}
                >
                    <Typography variant='h5' color='primary' sx={{ mr: 2, mt: 0.5 }}>
                        Uncertainty Analysis
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Intro />
                </AccordionDetails>
            </Accordion>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                <Box sx={{ flexGrow: 1 }}></Box>
                <TextField
                    label='Filter'
                    aria-label='search field'
                    type='text'
                    variant='outlined'
                    size='small'
                    id={`search-box-${uuidRef.current}`}
                    onChange={onSearchTextChange}
                    sx={{
                        ml: 1, mr: 1, mt: 1,
                    }}

                    slotProps={{
                        input: {
                            'role': 'searchbox',
                            'tabIndex': 0,
                            'aria-labelledby': `search-box-${uuidRef.current}`,
                            'aria-label': 'search',
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label='search icon' onClick={():void  => {}}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                        
            </Box>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)'}}>
                <Table stickyHeader aria-label="sticky table"  className='table'>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                column.id === 'histogram' ?
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        style={{ minWidth: column.minWidth }}
                                    > {column.label} </TableCell>
                                    :
                                    <SortableCell
                                        key={column.id}
                                        column={column}
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                    />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {uncertaintyAnalysisResults.map((row) => {
                            const labels = getLabels(row.histogram);
                            return (
                                <TableRow
                                    key={row.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>{row.uniqueId}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.pedigree}</TableCell>
                                    <TableCell  align='center'>{Number.parseFloat(row.baseline.toString()).toExponential(2)}</TableCell>
                                    <TableCell align='center'>{Number.parseFloat(row.mean.toString()).toExponential(2)}</TableCell>
                                    <TableCell align='center'>{Number.parseFloat(row.standardDeviation.toString()).toExponential(2)}</TableCell>
                                    <TableCell align='center'>
                                        <Chip 
                                            label={Number.parseFloat(row.coefficientOfVariation.toFixed(2))}
                                            color={categorizeCV(row.coefficientOfVariation)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div style={{ position: 'relative', width: '100%', height: '150px' }} id={`${row.id}-${uuidRef.current}`}>
                                            <IconButton
                                                title={fullscreenMode ? 'Exit full screen mode' : 'View distribution in full screen mode' }
                                                aria-label='view distribution'
                                                onClick={(): void => {
                                                    let elem = document.getElementById(`${row.id}-${uuidRef.current}`);
                                                    if(elem?.requestFullscreen) {
                                                        elem.requestFullscreen().then(() => {
                                                            setFullscreenMode(true);
                                                        });
                                                    } 
                                                    if(document.fullscreenElement) {
                                                        document.exitFullscreen().then(() => {
                                                            setFullscreenMode(false);
                                                        });
                                                    } 

                                                }}
                                                style={{position: 'absolute', top: 0, right: 0}}
                                            >
                                                <Fullscreen style={{ backgroundColor: 'white', color: 'black' }} /> 
                                            </IconButton>
                                            <Bar                                               
                                                data={{
                                                    labels: labels,
                                                    datasets: [{
                                                        label: 'Count',
                                                        data: Object.values(row.histogram.bins),
                                                        backgroundColor: labels.map((label: string) => {
                                                            const [lowerBound, upperBound]  = label.slice(1, label.length - 1).split(',').map(parseFloat);
                                                            if (lowerBound <= row.mean && row.mean <= upperBound) {
                                                                return 'red';
                                                            } else {
                                                                return 'blue';
                                                            }
                                                        }),
                                                        barThickness: 4,
                                                    }]
                                                }}
                                                options={{
                                                    animation: false,
                                                    scales: {
                                                        x: {
                                                            afterTickToLabelConversion(axis: any): void {
                                                                axis.ticks = [];
                                                            }
                                                        },
                                                        y: {
                                                            afterTickToLabelConversion(axis: any): void {
                                                                axis.ticks = [];
                                                            }
                                                        }
                                                    }
                                                }}
                                                style={{position: 'absolute', bottom: 0, left: 0}}
                                            />

                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>


        </Paper>
    );
}